import api from "./api";
import type { PortalPayrollFieldKey } from "../components/ui/groups/portalPayrollExcel";
import { readPortalPayrollError } from "./portalPayrollErrors";
import { getPortalPayrollHeader, normalizePortalPayrollBlob } from "./portalPayrollResponse";

export interface PortalPayrollPayload {
  fileName: string;
  fileSize: number;
  sheetName: string;
  headerRow: number;
  mapping: Array<{ source: string; target: PortalPayrollFieldKey }>;
  rows: Record<string, string | number | boolean>[];
}

export interface PortalPayrollSummary {
  total: number;
  complete: number;
  incomplete: number;
  missing: number;
  invalid: number;
}

export interface PortalPayrollResult {
  blob: Blob;
  filename: string;
  processId: string;
  summary: PortalPayrollSummary;
}

const numericHeader = (headers: Record<string, unknown>, key: string): number =>
  Number(getPortalPayrollHeader(headers, key) ?? 0) || 0;

const getFilename = (headers: Record<string, unknown>): string => {
  const explicit = getPortalPayrollHeader(headers, "x-filename");
  if (typeof explicit === "string" && explicit) return explicit;
  const disposition = getPortalPayrollHeader(headers, "content-disposition");
  if (typeof disposition === "string") {
    const match = /filename\*?=(?:UTF-8'')?["']?([^;"'\r\n]+)["']?/i.exec(disposition);
    if (match?.[1]) return decodeURIComponent(match[1]);
  }
  return `RESULTADO_NOMINA_PORTALES_${new Date().toISOString().slice(0, 10)}.xlsx`;
};

export const processPortalPayroll = async (payload: PortalPayrollPayload): Promise<PortalPayrollResult> => {
  const userId = sessionStorage.getItem("usuarioId") ?? "";
  if (!userId) throw new Error("No se pudo identificar al usuario de la sesión.");
  let response: any;
  try {
    response = await api.post(
      `excel/process/nomina-portales?userId=${encodeURIComponent(userId)}`,
      payload,
      { responseType: "blob" }
    );
  } catch (error) {
    throw new Error(await readPortalPayrollError(error));
  }

  const headers = response.headers as Record<string, unknown>;
  const blob = await normalizePortalPayrollBlob(response.data);
  const processId = getPortalPayrollHeader(headers, "x-process-id");
  return {
    blob,
    filename: getFilename(headers),
    processId: typeof processId === "string" ? processId : "",
    summary: {
      total: numericHeader(headers, "x-total-rows"),
      complete: numericHeader(headers, "x-total-complete"),
      incomplete: numericHeader(headers, "x-total-incomplete"),
      missing: numericHeader(headers, "x-total-missing"),
      invalid: numericHeader(headers, "x-total-invalid"),
    },
  };
};

export const downloadPortalPayrollResult = (result: Pick<PortalPayrollResult, "blob" | "filename">): void => {
  const url = URL.createObjectURL(result.blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = result.filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
